import { Box, InputGroup, InputLeftAddon, Select, useToast, Text } from "@chakra-ui/react";
import * as React from "react";
import { useGetOrganizationStructure } from "../../../../../hooks/query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ByYearChart } from "./components";
import {
  useGetLastThreeYearsEmissions,
  useGetLastThreeYearsEmissionsItemsCount
} from "../../../../../hooks/query/reports";
import { hideAppSpinner } from "../../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { showErrorToast } from "../../../../../utils";
import { mapYearsData } from "../helpers";
import { Paginator } from "../../../../../components/Paginator";

function ByYearTab() {
  const { t } = useTranslation("translation", {
    keyPrefix: "companyEmissionsDashboard"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();
  const dispatch = useDispatch();

  const [paginatorObj, setPaginatorObj] = useState({
    queryPageIndex: 0,
    totalPages: 0
  });
  const queryPageSize = 10;

  const [chartData, setChartData] = useState([]);

  const handlePageSelection = (newPage) => {
    setChartData([]);
    setPaginatorObj({
      ...paginatorObj,
      queryPageIndex: newPage - 1
    });
  };

  const {
    data: organizationStructure
  } = useGetOrganizationStructure({
    onSuccess: (data) => {
      if (typeof selectedDimension === "undefined" || !selectedDimension) {
        setSelectedDimension(data.dimensions?.dimensionOne?.id ?? null);
      }
    },
    refetchOnWindowFocus: false
  });

  const [selectedDimension, setSelectedDimension] = useState(
    organizationStructure?.dimensions?.dimensionOne?.id ?? null);

  const currentYear = new Date().getFullYear();
  const yearsKeys = [currentYear - 3, currentYear - 2, currentYear - 1, currentYear];

  const {
    isFetching: isItemsCountFetching
  } = useGetLastThreeYearsEmissionsItemsCount(selectedDimension, {
    enabled: selectedDimension !== null && typeof selectedDimension !== "undefined",
    onSuccess: (data) => {
      if (data !== null && typeof data !== "undefined") {
        setPaginatorObj({
          ...paginatorObj,
          totalPages: Math.ceil(data / queryPageSize)
        });
      }
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const {
    isFetching: isChartDataFetching
  } = useGetLastThreeYearsEmissions(selectedDimension, { page: paginatorObj.queryPageIndex, size: queryPageSize }, {
    enabled: selectedDimension !== null && typeof selectedDimension !== "undefined",
    onSuccess: (data) => {
      if (data && data.length > 0) {
        const mappedData = mapYearsData(data, yearsKeys);
        setChartData(mappedData);
      } else {
        setChartData([]);
      }
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  return (
    <Box>
      {organizationStructure ?
        <InputGroup display="flex" alignItems="center" width="fit-content" marginBlock={4}>
          <InputLeftAddon children={t("byDimension.dimension")} fontWeight="semibold" />
          <Select
            backgroundColor="white"
            borderRadius="none"
            value={selectedDimension}
            onChange={(e) => {
              setChartData([]);
              setPaginatorObj({
                queryPageIndex: 0,
                totalPages: 0
              });
              setSelectedDimension(e.target.value);
            }}
          >
            {Object.keys(organizationStructure.dimensions)
              .filter(key => organizationStructure.dimensions[key] !== null)
              .sort((a, b) => {
                return (organizationStructure.dimensions[a].id > organizationStructure.dimensions[b].id) ? 1 :
                  ((organizationStructure.dimensions[b].id > organizationStructure.dimensions[a].id) ? -1 : 0);
              })
              .map(dimKey => {
                return <option
                  key={`by-year-tab-dim-key-${organizationStructure.dimensions[dimKey].id}`}
                  value={organizationStructure.dimensions[dimKey].id}
                >
                  {organizationStructure.dimensions[dimKey].name}
                </option>;
              })}
          </Select>
        </InputGroup> : null
      }
      {paginatorObj && paginatorObj.totalPages !== 0 ?
        <Paginator
          selectedPage={paginatorObj.queryPageIndex + 1}
          numberOfPages={paginatorObj.totalPages}
          handlePageSelection={(newPage) => handlePageSelection(newPage)}
        /> : null
      }
      {!isItemsCountFetching && !isChartDataFetching && chartData && chartData.length > 0 ?
        <ByYearChart chartData={chartData} yearsKeys={yearsKeys} /> :
        !isItemsCountFetching && !isChartDataFetching && (!chartData || chartData.length === 0) ?
          <Text fontSize="lg" fontWeight="bold">
            {globalTranslation("noDataAvailable")}
          </Text> : null
      }
    </Box>
  );
}

export default ByYearTab;